/**
 * GoldenMeadHall — Thornhaven's grand civic landmark (Gothic / Victorian /
 * Norse-fantasy hybrid). Replaces the old procedural NorseLonghouse entirely.
 *
 * Composition (bottom → top):
 *   - ashlar STONE ground storey with tall Gothic lancet windows + buttresses
 *   - jettied TIMBER-FRAMED upper storey (dark trim over warm walls + plaster)
 *   - steep SLATE gable roof with a rose-window south gable + ridge finial
 *   - four corner TURRETS with tall spires → strong, readable silhouette
 *   - a projecting pointed-arch ENTRANCE PORCH with double doors, steps,
 *     flanking braziers and an integrated gold "GOLDEN MEAD HALL" sign
 *
 * Placement: the world group sits at (10,0,0) rot 0 so the entrance stays on
 * the +Z (south) face at world (10,0,8) — exactly where the wired door
 * interaction / exit spawn live. The long elegant west side fronts the plaza /
 * Yggdrasil hub. Collision is one solid footprint box (entry is via the
 * proximity prompt, matching the previous hall's behaviour).
 */

import { TH_STYLE } from './thornhavenStyle';
import { RigidBody, CuboidCollider } from '@react-three/rapier';
import * as THREE from 'three';
import { useMemo } from 'react';
import { BuildingSign } from './BuildingSign';

const HW = 6;      // half-width (X)
const HD = 8;      // half-depth (Z); front face at local z = +8
const BASE_H = 1.0;
const STONE_TOP = 5.0;   // top of ground stone storey
const TIMBER_TOP = 8.0;  // top of upper timber storey (eave)
const RISE = 4.6;        // roof rise above eave
const RIDGE_Y = TIMBER_TOP + RISE;

// Gothic lancet window: lit pane, leaded mullions, proud stone surround + a
// pointed stone cap for the Gothic silhouette.
function Lancet({ position, rotationY = 0, w = 0.85, h = 2.2 }) {
  const mull = [];
  for (let i = -1; i <= 1; i++) mull.push((h / 4) * i);
  return (
    <group position={position} rotation={[0, rotationY, 0]}>
      <mesh castShadow receiveShadow>
        <boxGeometry args={[w + 0.34, h + 0.3, 0.16]} />
        <meshStandardMaterial color={TH_STYLE.stoneLight} roughness={0.9} />
      </mesh>
      <mesh position={[0, h / 2 + 0.05, 0]} rotation={[0, 0, Math.PI / 4]} castShadow>
        <boxGeometry args={[(w + 0.34) * 0.707, (w + 0.34) * 0.707, 0.16]} />
        <meshStandardMaterial color={TH_STYLE.stoneLight} roughness={0.9} />
      </mesh>
      <mesh position={[0, 0, 0.1]}>
        <planeGeometry args={[w, h]} />
        <meshStandardMaterial
          color={TH_STYLE.window}
          emissive={TH_STYLE.window}
          emissiveIntensity={0.65}
          roughness={0.4}
        />
      </mesh>
      <mesh position={[0, 0, 0.12]}>
        <boxGeometry args={[0.05, h, 0.04]} />
        <meshStandardMaterial color={TH_STYLE.timberTrim} />
      </mesh>
      {mull.map((y, i) => (
        <mesh key={i} position={[0, y, 0.12]}>
          <boxGeometry args={[w, 0.05, 0.04]} />
          <meshStandardMaterial color={TH_STYLE.timberTrim} />
        </mesh>
      ))}
    </group>
  );
}

function Brazier({ position }) {
  return (
    <group position={position}>
      <mesh position={[0, 0.9, 0]} castShadow>
        <cylinderGeometry args={[0.1, 0.14, 1.8, 8]} />
        <meshStandardMaterial color={TH_STYLE.timberTrim} roughness={0.8} />
      </mesh>
      <mesh position={[0, 1.85, 0]} castShadow>
        <cylinderGeometry args={[0.32, 0.18, 0.4, 10]} />
        <meshStandardMaterial color={TH_STYLE.goldDark} metalness={0.5} roughness={0.5} />
      </mesh>
      <mesh position={[0, 2.2, 0]}>
        <coneGeometry args={[0.26, 0.6, 10]} />
        <meshBasicMaterial color={TH_STYLE.flame} />
      </mesh>
      <pointLight position={[0, 2.3, 0]} color={TH_STYLE.flame} intensity={6} distance={9} decay={2} />
    </group>
  );
}

// Corner turret: stone shaft + tall slate spire + gold finial.
function Turret({ x, z }) {
  return (
    <group position={[x, 0, z]}>
      <mesh position={[0, 4.5, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[0.72, 0.8, 9, 12]} />
        <meshStandardMaterial color={TH_STYLE.stone} roughness={0.92} />
      </mesh>
      {/* corbel ring */}
      <mesh position={[0, 9, 0]} castShadow>
        <cylinderGeometry args={[0.9, 0.78, 0.5, 12]} />
        <meshStandardMaterial color={TH_STYLE.stoneDark} roughness={0.9} />
      </mesh>
      {/* spire */}
      <mesh position={[0, 10.9, 0]} castShadow>
        <coneGeometry args={[0.95, 3.0, 12]} />
        <meshStandardMaterial color={TH_STYLE.roofDark} roughness={0.85} />
      </mesh>
      {/* gold finial */}
      <mesh position={[0, 12.6, 0]} castShadow>
        <sphereGeometry args={[0.16, 10, 10]} />
        <meshStandardMaterial color={TH_STYLE.gold} metalness={0.7} roughness={0.3} />
      </mesh>
    </group>
  );
}

export default function GoldenMeadHall({ position = [10, 0, 0], rotation = [0, 0, 0] }) {
  const pitch = Math.atan2(RISE, HW);
  const slopeLen = Math.sqrt(HW * HW + RISE * RISE) + 0.7;
  const roofWidthZ = HD * 2 + 1.4;

  // Gable triangle (fills eave→ridge at the Z ends)
  const gable = useMemo(() => {
    const s = new THREE.Shape();
    s.moveTo(-HW - 0.2, 0);
    s.lineTo(HW + 0.2, 0);
    s.lineTo(0, RISE + 0.2);
    s.lineTo(-HW - 0.2, 0);
    return s;
  }, []);

  const sideWindowZ = [-4.2, 0, 4.2];

  return (
    <group position={position} rotation={rotation}>
      {/* ===== COLLISION (solid footprint; entry via proximity prompt) ===== */}
      <RigidBody type="fixed" colliders={false}>
        <CuboidCollider args={[HW + 0.3, 4.5, HD + 0.3]} position={[0, 4.5, 0]} />
      </RigidBody>

      {/* ===== PLINTH ===== */}
      <mesh position={[0, BASE_H / 2, 0]} castShadow receiveShadow>
        <boxGeometry args={[HW * 2 + 0.7, BASE_H, HD * 2 + 0.7]} />
        <meshStandardMaterial color={TH_STYLE.stoneDark} roughness={0.95} />
      </mesh>

      {/* ===== GROUND STONE STOREY ===== */}
      <mesh position={[0, (STONE_TOP + BASE_H) / 2, 0]} castShadow receiveShadow>
        <boxGeometry args={[HW * 2, STONE_TOP - BASE_H, HD * 2]} />
        <meshStandardMaterial color={TH_STYLE.stone} roughness={0.9} />
      </mesh>
      {/* quoin corner posts */}
      {[[-HW, -HD], [HW, -HD], [-HW, HD], [HW, HD]].map(([x, z], i) => (
        <mesh key={`q-${i}`} position={[x, (STONE_TOP + BASE_H) / 2, z]} castShadow>
          <boxGeometry args={[0.6, STONE_TOP - BASE_H, 0.6]} />
          <meshStandardMaterial color={TH_STYLE.stoneLight} roughness={0.85} />
        </mesh>
      ))}
      {/* buttresses on the long (plaza-facing + east) walls */}
      {[-HW - 0.25, HW + 0.25].map((x, xi) =>
        [-3.5, 3.5].map((z, zi) => (
          <mesh key={`bt-${xi}-${zi}`} position={[x, (STONE_TOP + BASE_H) / 2 - 0.4, z]} castShadow>
            <boxGeometry args={[0.5, STONE_TOP - BASE_H - 0.8, 0.9]} />
            <meshStandardMaterial color={TH_STYLE.stoneDark} roughness={0.92} />
          </mesh>
        ))
      )}
      {/* water-table string course */}
      <mesh position={[0, STONE_TOP, 0]} castShadow>
        <boxGeometry args={[HW * 2 + 0.5, 0.3, HD * 2 + 0.5]} />
        <meshStandardMaterial color={TH_STYLE.stoneLight} roughness={0.85} />
      </mesh>

      {/* lancet windows — west (plaza), east, and flanking the south entrance */}
      {sideWindowZ.map((z, i) => (
        <Lancet key={`w-${i}`} position={[-HW - 0.09, 2.9, z]} rotationY={-Math.PI / 2} />
      ))}
      {sideWindowZ.map((z, i) => (
        <Lancet key={`e-${i}`} position={[HW + 0.09, 2.9, z]} rotationY={Math.PI / 2} />
      ))}
      <Lancet position={[-3.1, 2.9, HD + 0.09]} />
      <Lancet position={[3.1, 2.9, HD + 0.09]} />

      {/* ===== UPPER TIMBER STOREY (jettied) ===== */}
      <mesh position={[0, (TIMBER_TOP + STONE_TOP) / 2, 0]} castShadow receiveShadow>
        <boxGeometry args={[HW * 2 + 0.4, TIMBER_TOP - STONE_TOP, HD * 2 + 0.4]} />
        <meshStandardMaterial color={TH_STYLE.timber} roughness={0.85} />
      </mesh>
      {/* dark timber framing on the long faces: top/bottom plates + studs + braces */}
      {[-HW - 0.21, HW + 0.21].map((x, xi) => (
        <group key={`tf-${xi}`} rotation={[0, xi === 0 ? -Math.PI / 2 : Math.PI / 2, 0]} position={[x, 0, 0]}>
          {/* plates */}
          {[STONE_TOP + 0.15, TIMBER_TOP - 0.15].map((y, yi) => (
            <mesh key={yi} position={[0, y, 0]}>
              <boxGeometry args={[HD * 2 + 0.4, 0.22, 0.08]} />
              <meshStandardMaterial color={TH_STYLE.timberTrim} roughness={0.8} />
            </mesh>
          ))}
          {/* studs */}
          {[-6, -3, 0, 3, 6].map((z, zi) => (
            <mesh key={`s${zi}`} position={[z, (TIMBER_TOP + STONE_TOP) / 2, 0]}>
              <boxGeometry args={[0.18, TIMBER_TOP - STONE_TOP, 0.08]} />
              <meshStandardMaterial color={TH_STYLE.timberTrim} roughness={0.8} />
            </mesh>
          ))}
          {/* diagonal braces */}
          {[-4.5, 4.5].map((z, zi) => (
            <mesh key={`d${zi}`} position={[z, (TIMBER_TOP + STONE_TOP) / 2, 0.01]} rotation={[0, 0, zi === 0 ? 0.6 : -0.6]}>
              <boxGeometry args={[0.16, 2.6, 0.07]} />
              <meshStandardMaterial color={TH_STYLE.timberTrim} roughness={0.8} />
            </mesh>
          ))}
        </group>
      ))}

      {/* ===== ROOF (steep slate gable, ridge along Z) ===== */}
      <mesh position={[-HW / 2, TIMBER_TOP + RISE / 2, 0]} rotation={[0, 0, pitch]} castShadow receiveShadow>
        <boxGeometry args={[slopeLen, 0.28, roofWidthZ]} />
        <meshStandardMaterial color={TH_STYLE.roof} roughness={0.8} />
      </mesh>
      <mesh position={[HW / 2, TIMBER_TOP + RISE / 2, 0]} rotation={[0, 0, -pitch]} castShadow receiveShadow>
        <boxGeometry args={[slopeLen, 0.28, roofWidthZ]} />
        <meshStandardMaterial color={TH_STYLE.roof} roughness={0.8} />
      </mesh>
      {/* ridge beam + ridge finial */}
      <mesh position={[0, RIDGE_Y + 0.05, 0]} castShadow>
        <boxGeometry args={[0.4, 0.4, roofWidthZ]} />
        <meshStandardMaterial color={TH_STYLE.roofRidge} roughness={0.8} />
      </mesh>
      {[-HD - 0.5, HD + 0.5].map((z, i) => (
        <mesh key={`rf-${i}`} position={[0, RIDGE_Y + 0.7, z]} castShadow>
          <coneGeometry args={[0.22, 1.4, 8]} />
          <meshStandardMaterial color={TH_STYLE.gold} metalness={0.6} roughness={0.35} />
        </mesh>
      ))}

      {/* gable ends */}
      <group position={[0, TIMBER_TOP, HD]}>
        <mesh castShadow>
          <shapeGeometry args={[gable]} />
          <meshStandardMaterial color={TH_STYLE.plaster} roughness={0.9} side={THREE.DoubleSide} />
        </mesh>
        {/* gable timber trim (inverted V) */}
        <mesh position={[-HW / 2, RISE / 2, 0.06]} rotation={[0, 0, pitch]}>
          <boxGeometry args={[slopeLen - 0.7, 0.18, 0.08]} />
          <meshStandardMaterial color={TH_STYLE.timberTrim} />
        </mesh>
        <mesh position={[HW / 2, RISE / 2, 0.06]} rotation={[0, 0, -pitch]}>
          <boxGeometry args={[slopeLen - 0.7, 0.18, 0.08]} />
          <meshStandardMaterial color={TH_STYLE.timberTrim} />
        </mesh>
        {/* rose window */}
        <mesh position={[0, RISE * 0.45, 0.06]}>
          <circleGeometry args={[0.85, 24]} />
          <meshStandardMaterial color={TH_STYLE.window} emissive={TH_STYLE.window} emissiveIntensity={0.6} roughness={0.4} />
        </mesh>
        <mesh position={[0, RISE * 0.45, 0.08]}>
          <torusGeometry args={[0.85, 0.08, 8, 24]} />
          <meshStandardMaterial color={TH_STYLE.stoneLight} roughness={0.85} />
        </mesh>
        {[0, Math.PI / 3, (2 * Math.PI) / 3].map((a, i) => (
          <mesh key={i} position={[0, RISE * 0.45, 0.09]} rotation={[0, 0, a]}>
            <boxGeometry args={[1.7, 0.06, 0.03]} />
            <meshStandardMaterial color={TH_STYLE.timberTrim} />
          </mesh>
        ))}
      </group>
      <group position={[0, TIMBER_TOP, -HD]} rotation={[0, Math.PI, 0]}>
        <mesh castShadow>
          <shapeGeometry args={[gable]} />
          <meshStandardMaterial color={TH_STYLE.plaster} roughness={0.9} side={THREE.DoubleSide} />
        </mesh>
      </group>

      {/* ===== ENTRANCE PORCH (south, projecting) ===== */}
      <group position={[0, 0, HD]}>
        {/* stone jambs */}
        {[-1.5, 1.5].map((x, i) => (
          <mesh key={i} position={[x, 2.0, 1.0]} castShadow receiveShadow>
            <boxGeometry args={[0.7, 4.0, 2.0]} />
            <meshStandardMaterial color={TH_STYLE.stone} roughness={0.9} />
          </mesh>
        ))}
        {/* pointed arch cap over the doorway */}
        <mesh position={[0, 4.0, 1.0]} rotation={[0, 0, Math.PI / 4]} castShadow>
          <boxGeometry args={[2.3, 2.3, 2.0]} />
          <meshStandardMaterial color={TH_STYLE.stone} roughness={0.9} />
        </mesh>
        {/* dark recess behind doors */}
        <mesh position={[0, 1.9, 0.9]}>
          <boxGeometry args={[2.2, 3.4, 0.2]} />
          <meshStandardMaterial color="#060606" />
        </mesh>
        {/* double doors */}
        {[-0.55, 0.55].map((x, i) => (
          <mesh key={i} position={[x, 1.75, 1.05]} castShadow>
            <boxGeometry args={[1.0, 3.2, 0.14]} />
            <meshStandardMaterial color={TH_STYLE.door} roughness={0.8} />
          </mesh>
        ))}
        {/* iron door bands + gold ring handles */}
        {[1.1, 2.4].map((y, i) => (
          <mesh key={`band${i}`} position={[0, y, 1.14]}>
            <boxGeometry args={[2.1, 0.12, 0.03]} />
            <meshStandardMaterial color={TH_STYLE.timberTrim} metalness={0.4} roughness={0.6} />
          </mesh>
        ))}
        {[-0.2, 0.2].map((x, i) => (
          <mesh key={`h${i}`} position={[x, 1.75, 1.16]} rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[0.12, 0.03, 8, 16]} />
            <meshStandardMaterial color={TH_STYLE.gold} metalness={0.7} roughness={0.3} />
          </mesh>
        ))}
        {/* porch gable roof */}
        <mesh position={[-0.9, 5.0, 1.0]} rotation={[0, 0, 0.8]} castShadow>
          <boxGeometry args={[2.6, 0.22, 2.6]} />
          <meshStandardMaterial color={TH_STYLE.roof} roughness={0.8} />
        </mesh>
        <mesh position={[0.9, 5.0, 1.0]} rotation={[0, 0, -0.8]} castShadow>
          <boxGeometry args={[2.6, 0.22, 2.6]} />
          <meshStandardMaterial color={TH_STYLE.roof} roughness={0.8} />
        </mesh>
        {/* stone steps */}
        {[0, 1, 2].map((i) => (
          <mesh key={`st${i}`} position={[0, 0.12 - i * 0.12, 2.0 + i * 0.5]} receiveShadow>
            <boxGeometry args={[3.4 + i * 0.4, 0.24, 0.5]} />
            <meshStandardMaterial color={TH_STYLE.stoneLight} roughness={0.9} />
          </mesh>
        ))}
        {/* flanking braziers */}
        <Brazier position={[-2.3, 0.3, 1.6]} />
        <Brazier position={[2.3, 0.3, 1.6]} />
        {/* integrated hanging sign */}
        <BuildingSign label="GOLDEN MEAD HALL" position={[0, 5.9, 1.4]} width={4.6} height={1.0} fontSize={0.42} hanging />
      </group>

      {/* ===== TURRETS ===== */}
      <Turret x={-HW - 0.2} z={-HD - 0.2} />
      <Turret x={HW + 0.2} z={-HD - 0.2} />
      <Turret x={-HW - 0.2} z={HD + 0.2} />
      <Turret x={HW + 0.2} z={HD + 0.2} />

      {/* warm entrance light */}
      <pointLight position={[0, 3.5, HD + 2.5]} color={TH_STYLE.window} intensity={5} distance={12} decay={2} />
    </group>
  );
}
